import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';

export interface Announcement {
  announcementId: string;
  title: string;
  message: string;
  displayFrom: string;
  displayTo: string;
  priority: 'normal' | 'critical';
  status?: 'active' | 'future' | 'expired';
  isRead?: boolean;
  attachmentKey?: string;
  attachmentName?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface AnnouncementFormData {
  title: string;
  message: string;
  displayFrom: string;
  displayTo: string;
  priority: 'normal' | 'critical';
  attachmentKey?: string;
  attachmentName?: string;
}

@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  constructor(
    private api: ApiService,
    private http: HttpClient,
  ) {}

  getAll(): Observable<Announcement[]> {
    return this.api.get<Announcement[]>('/announcements');
  }

  create(data: AnnouncementFormData): Observable<Announcement> {
    return this.api.post<AnnouncementFormData, Announcement>(
      '/announcements',
      data,
    );
  }

  update(
    id: string,
    data: Partial<AnnouncementFormData>,
  ): Observable<Announcement> {
    return this.api.put<Partial<AnnouncementFormData>, Announcement>(
      `/announcements/${id}`,
      data,
    );
  }

  delete(id: string): Observable<void> {
    return this.api.delete<void>(`/announcements/${id}`);
  }

  getUploadUrl(
    fileName: string,
    contentType: string,
  ): Observable<{ uploadUrl: string; fileKey: string }> {
    return this.api.get<{ uploadUrl: string; fileKey: string }>(
      '/announcements/upload-url',
      { fileName, contentType },
    );
  }

  getDownloadUrl(
    fileKey: string,
  ): Observable<{ downloadUrl: string; fileName: string }> {
    return this.api.get<{ downloadUrl: string; fileName: string }>(
      '/announcements/download-url',
      { fileKey },
    );
  }

  getUnreadCount(): Observable<{ count: number }> {
    return this.api.get<{ count: number }>('/announcements/unread-count');
  }

  markAsRead(id: string): Observable<void> {
    return this.api.post<undefined, void>(`/announcements/${id}/read`);
  }

  markAllAsRead(): Observable<{ markedCount: number }> {
    return this.api.post<undefined, { markedCount: number }>(
      '/announcements/mark-all-read',
    );
  }

  uploadFile(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });
  }
}
